import TextUtil from "../components/text/TextUtil";
import TextWrap from "../components/text/TextWrap";
import { containsCJKCharacters, containsZWSCharacters } from "../util/StringUtil";
import { isNull } from "../util/TypeUtil";

/**
 * Enables a createjs.Text to fit its text under a given size by adjusting reducing its size metrics.
 * 
 * Current limitations:
 * - It starts to reduce the font size from a given maximum font size.
 */
export default class TextFitter {
    /**
     * 
     * @param {createjs.Text} textField 
     * @param {Object} options Optional values.
     * @param {boolean} options.enableAutoSize sets if the auto size is enabled
     * @param {string} options.wordWrapStyle the type of word wrapping (none, soft, or hard)
     * @param {number} options.maxHeight the maximum height of the text. If not supplied, uses the original measured height of the text.
     * @param {number} options.maxWidth the maximum width of the text. If not supplied, uses the original line width
     * @param {number} options.minFontSize minimum size that the font can reduce to.
     * @param {number} options.maxFontSize the maximum or starting size from which the font starts reducing from. If not supplied, uses the text's original value.
     * @param {number} options.fontStep the value to reduce the font at each step.
     * @param {number} options.maxLineHeight the maximum or starting line height from which it starts reducing from. If not supplied, uses the text's original value.
     * @param {number} options.minLineHeight the minimum size that the line height can reduce to.
     * @param {number} options.lineHeightStep the value to reduce the line height at each step. If 0, it does not reduce line height.
     */
    constructor(textField, options = TextFitter.DEFAULT_OPTIONS) {
        this._textField = textField;
        this._originalText = textField.text;

        this._enableAutoSize = !isNull(options.enableAutoSize) ? options.enableAutoSize : TextFitter.DEFAULT_OPTIONS.enableAutoSize;
        this._wordWrapStyle = options.wordWrapStyle || TextFitter.DEFAULT_OPTIONS.wordWrapStyle;
        
        // NB: `originalMeasuredHeight` in the SkinFactory when mapping fonts
        this._maxHeight = options.maxHeight || this._textField.originalMeasuredHeight || this._textField.getMeasuredHeight();
        this._maxWidth = options.maxWidth || this._textField.lineWidth;

        this._minFontSize = options.minFontSize || TextFitter.DEFAULT_OPTIONS.minFontSize;
        this._maxFontSize = options.maxFontSize || TextUtil.getFontSize(this._textField);
        this._fontStep = options.fontStep || TextFitter.DEFAULT_OPTIONS.fontStep;

        this._updateLineHeight = options.updateLineHeight || TextFitter.DEFAULT_OPTIONS.updateLineHeight;
        this._maxLineHeight = options.maxLineHeight || this._textField.lineHeight;
        this._minLineHeight = options.minLineHeight || TextFitter.DEFAULT_OPTIONS.minLineHeight;
        this._lineHeightStep = options.lineHeightStep || TextFitter.DEFAULT_OPTIONS.lineHeightStep;
    }

    /**
     * Sets a new text for the text field and updates its fit
     * @param {string} text 
     */
    setText(text) {
        this._originalText = text;
        this._textField.text = text;
        this.update();
    }

    /**
     * Updates the text field font metrics to ensure the text fits 
     */
    update() {
        const isWordWrapActive = this._isWordWrapActive();
        const isLineHeightUpdateActive = this._wordWrapStyle !== TextFitter.WRAP_NONE && this._lineHeightStep > 0;
        // If enabled first apply word wrap, this allows to get the proper text height to 
        // text that does not word wrap by createjs (e.g., japanese or chinese due to lack of
        // space symbols).
        if(isWordWrapActive) {
            this._updateWordWrap();
        }

        if(this._enableAutoSize) {
            let fontSize = this._maxFontSize;
            let lineHeight = this._maxLineHeight;

            while(this._isOutOfBounds() && fontSize > this._minFontSize) {
                // decrease font size and apply to text
                fontSize = Math.max(this._minFontSize, fontSize - this._fontStep);
                TextUtil.setFontSize(this._textField, fontSize);
                // apply word wrap if enabled
                if(isWordWrapActive) {
                    this._updateWordWrap();
                }

                // if enabled, decrease line height
                if(isLineHeightUpdateActive && lineHeight > this._minLineHeight){
                    lineHeight = Math.max(this._minLineHeight, lineHeight - this._lineHeightStep);
                    this._textField.lineHeight = lineHeight;
                }
            }
        }
    }

    _isWordWrapActive() {
        const isActive = this._wordWrapStyle === TextFitter.WRAP_HARD || 
            (this._wordWrapStyle === TextFitter.WRAP_SOFT && (containsCJKCharacters(this._originalText) || containsZWSCharacters(this._originalText)));
        return isActive;
    }

    _isOutOfBounds() {
        if(this._textField.getMeasuredHeight() > this._maxHeight){
            return true;
        } else if(this._wordWrapStyle === TextFitter.WRAP_NONE) {
            // NB: getMeasuredWidth() returns the measured, untransformed width of the text without wrapping.
            // See: https://createjs.com/docs/easeljs/classes/Text.html#method_getMeasuredWidth
            return this._textField.getMeasuredWidth() > this._maxWidth;
        } else {
            // NB: getBounds() provides the text width based on the wrapped text
            return this._textField.getBounds().width > this._maxWidth;
        }
    }

    _updateWordWrap() {
        // NB: apply original as a previous wrap operation may have added newlines 
        // and disrupt proper wrapping
        this._textField.text = this._originalText;
        TextWrap.wrapTextField(this._textField);
    }
}

TextFitter.WRAP_SOFT = "TextFitter.WRAP_SOFT";
TextFitter.WRAP_HARD = "TextFitter.WRAP_HARD";
TextFitter.WRAP_NONE = "TextFitter.WRAP_NONE";

TextFitter.DEFAULT_OPTIONS = {
    enableAutoSize: true,
    wordWrapStyle: TextFitter.WRAP_SOFT,

    minFontSize: 2, 
    maxFontSize: 0,
    fontStep: 2, 
    maxHeight: 0,

    maxLineHeight: 0, 
    minLineHeight: 2,
    lineHeightStep: 2
};